type RawRecord = Record<string, unknown>

export type TrellisEntityTypeRef = string | { id?: string; name?: string }

export type TrellisEntityReference = {
  id: string
  typeId?: string
  typeName?: string
  primaryLabel?: string
  secondaryLabel?: string
}

export type TrellisEntityFieldValue =
  | { kind: 'null'; value: null; text: '' }
  | { kind: 'text'; value: string; text: string }
  | { kind: 'number'; value: number; text: string }
  | { kind: 'date'; value: string; text: string }
  | {
      kind: 'reference'
      value: string
      text: string
      referenced?: TrellisEntityReference
    }

export type TrellisEntityField = {
  id: string
  name: string
  type: string
  required: boolean
  value: TrellisEntityFieldValue
}

export type TrellisEntity = {
  id: string
  type: {
    id: string
    name: string
    kind?: string
  }
  primaryLabel: string
  secondaryLabel?: string
  fields: TrellisEntityField[]
  valuesByFieldId: Record<string, TrellisEntityFieldValue>
  valuesByFieldName: Record<string, TrellisEntityFieldValue>
}

export type TrellisEntityType = {
  id: string
  name: string
  kind?: string
  fields: Array<{
    id: string
    name: string
    type: string
    required: boolean
    referenceTypeIds?: string[]
  }>
}

export type TrellisEntityListInput = {
  type: TrellisEntityTypeRef
  query?: string
  limit?: number
  cursor?: string
  includeRelated?: boolean
}

export type TrellisEntityListResult = {
  items: TrellisEntity[]
  nextCursor?: string
}

export type TrellisEntitiesApi = {
  get(id: string, options?: { includeRelated?: boolean }): Promise<TrellisEntity | null>
  getMany(ids: string[], options?: { includeRelated?: boolean }): Promise<TrellisEntity[]>
  list(input: TrellisEntityListInput): Promise<TrellisEntityListResult>
  count(input: { type: TrellisEntityTypeRef; query?: string }): Promise<number>
  types: {
    get(ref: TrellisEntityTypeRef): Promise<TrellisEntityType | null>
    list(): Promise<TrellisEntityType[]>
  }
  me: {
    id?: string
    get(): Promise<TrellisEntity | null>
  }
}

type ResolvedEntityType = {
  raw: RawRecord
  snapshot: TrellisEntityType
}

type TrellisEntitiesAuth = {
  accessToken: string
  organizationId: string
  entityApiUrl: string
  userEntityId: string
}

type CreateTrellisEntitiesApiInput = {
  auth?: TrellisEntitiesAuth
  fetchImpl?: typeof fetch
}

const ENTITY_TYPES_PAGE_SIZE = 500
const ENTITIES_BATCH_LIMIT = 50
const ENTITY_LIST_DEFAULT_LIMIT = 50

const isRecord = (value: unknown): value is RawRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeId = (value: string) => value.trim().toLowerCase()

const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim())

const extractId = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (!isRecord(value)) return undefined
  if (typeof value.id === 'string' && value.id.trim()) return value.id.trim()
  if (typeof value.value === 'string' && value.value.trim()) return value.value.trim()
  return undefined
}

const readString = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

const readOptionalString = (...values: unknown[]) => {
  for (const value of values) {
    const resolved = readString(value)
    if (resolved) return resolved
  }
  return undefined
}

const unwrapEntity = (entity: RawRecord) => (isRecord(entity.entity) ? (entity.entity as RawRecord) : entity)

const getEntityId = (entity: RawRecord) =>
  extractId(entity.id) || extractId(entity.entityId) || extractId(entity.entity_id)

const getEntityTypeId = (entity: RawRecord): string | undefined => {
  const direct =
    extractId(entity.typeId) ||
    extractId(entity.type_id) ||
    extractId(entity.entityTypeId) ||
    extractId(entity.entity_type_id)
  if (direct) return direct
  return extractId(entity.type)
}

const getEntityLabel = (entity: RawRecord) => {
  const data = unwrapEntity(entity)
  return readOptionalString(data.primaryLabelText, data.primaryLabel, data.name, data.label, data.title, data.id) ?? ''
}

const getEntitySecondaryLabel = (entity: RawRecord) => {
  const data = unwrapEntity(entity)
  return readOptionalString(data.secondaryLabelText, data.secondaryLabel, data.subtitle, data.description)
}

const getDocuments = (value: unknown, key: 'documents' | 'relatedDocuments') => {
  if (!isRecord(value)) return []
  const items = value[key]
  if (!Array.isArray(items)) return []
  return items.filter(isRecord)
}

const getNextCursor = (value: unknown) => {
  if (!isRecord(value)) return undefined
  return readOptionalString(value.next, value.nextCursor)
}

const getTotalCount = (value: unknown) => {
  if (!isRecord(value) || typeof value.totalCount !== 'number' || !Number.isFinite(value.totalCount)) return 0
  return value.totalCount
}

const getRawFields = (entityType: RawRecord) => {
  const fields = entityType.fields
  if (!Array.isArray(fields)) return []
  return fields.filter(isRecord)
}

const getFieldValidations = (field: RawRecord) => {
  const validations = field.validations
  if (!Array.isArray(validations)) return []
  return validations.filter(isRecord)
}

const getFieldTypeNames = (field: RawRecord) => {
  if (typeof field.type === 'string' && field.type.trim()) return [field.type.trim()]
  if (typeof field.type === 'number' && Number.isFinite(field.type)) return [String(field.type)]

  const validation = getFieldValidations(field).find((entry) => readString(entry.type) === 'Validation.FieldType')
  if (!validation || !Array.isArray(validation.types)) return []
  return validation.types.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
}

const resolveFieldTypeName = (value: unknown) => {
  if (isRecord(value)) {
    const typeNames = getFieldTypeNames(value)
    const nonNullType = typeNames.find((typeName) => typeName !== 'Null')
    if (nonNullType) return nonNullType
    return typeNames[0] ?? ''
  }
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return ''
}

const isFieldRequired = (field: RawRecord) => {
  if (typeof field.required === 'boolean') return field.required
  const typeNames = getFieldTypeNames(field)
  if (typeNames.length === 0) return false
  return !typeNames.includes('Null')
}

const getReferenceTypeIds = (field: RawRecord) => {
  const candidates = [field.validReferenceTypes, field.referenceTypeIds, field.allowedEntityTypeIds]
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue
    const ids = candidate.map((entry) => extractId(entry)).filter((value): value is string => Boolean(value))
    if (ids.length > 0) return ids
  }

  const validation = getFieldValidations(field).find((entry) => readString(entry.type) === 'Validation.EntityType')
  if (validation && Array.isArray(validation.typeIds)) {
    return validation.typeIds.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
  }

  return []
}

const getRawFieldValue = (entity: RawRecord, fieldId: string) => {
  const values = entity.valuesByFieldId
  if (!isRecord(values)) return undefined
  return values[fieldId]
}

const createNullFieldValue = (): TrellisEntityFieldValue => ({ kind: 'null', value: null, text: '' })

const renderLabelTemplate = (format: string, values: string[]) => {
  if (!format.trim()) {
    return values.filter(Boolean).join(' ')
  }

  return format.replace(/\{(\d+)\}/g, (_match, rawIndex) => {
    const index = Number.parseInt(rawIndex, 10)
    if (!Number.isFinite(index) || index < 0) return ''
    return values[index] ?? ''
  })
}

const toReferenceSnapshot = (
  entity: RawRecord,
  relatedEntitiesById: ReadonlyMap<string, RawRecord>,
  resolvedEntityTypesById: ReadonlyMap<string, ResolvedEntityType>,
  entityTypesById: ReadonlyMap<string, TrellisEntityType>
): TrellisEntityReference | undefined => {
  const id = getEntityId(entity)
  if (!id) return undefined
  const typeId = getEntityTypeId(entity)
  const type = typeId ? resolvedEntityTypesById.get(normalizeId(typeId)) : undefined
  return {
    id,
    typeId,
    typeName: type?.snapshot.name,
    primaryLabel:
      (type
        ? getEntityLabelFromType(entity, type.raw, relatedEntitiesById, resolvedEntityTypesById, entityTypesById)
        : undefined) ||
      getEntityLabel(entity) ||
      undefined,
    secondaryLabel:
      (type
        ? getEntitySecondaryLabelFromType(
            entity,
            type.raw,
            relatedEntitiesById,
            resolvedEntityTypesById,
            entityTypesById
          )
        : undefined) || getEntitySecondaryLabel(entity),
  }
}

const toFieldValue = (
  rawValue: unknown,
  field: RawRecord,
  relatedEntitiesById: ReadonlyMap<string, RawRecord>,
  resolvedEntityTypesById: ReadonlyMap<string, ResolvedEntityType>,
  entityTypesById: ReadonlyMap<string, TrellisEntityType>
): TrellisEntityFieldValue => {
  if (rawValue === null || rawValue === undefined) {
    return createNullFieldValue()
  }

  const fieldType = resolveFieldTypeName(field).toLowerCase()
  if (fieldType === 'reference') {
    const referenceId = extractId(rawValue) ?? readString(rawValue)
    if (!referenceId) return createNullFieldValue()
    const referencedEntity = relatedEntitiesById.get(normalizeId(referenceId))
    const referenced = referencedEntity
      ? toReferenceSnapshot(referencedEntity, relatedEntitiesById, resolvedEntityTypesById, entityTypesById)
      : undefined
    return {
      kind: 'reference',
      value: referenceId,
      text:
        referenced?.primaryLabel ||
        readOptionalString(
          isRecord(rawValue) ? rawValue.text : undefined,
          isRecord(rawValue) ? rawValue.label : undefined,
          rawValue
        ) ||
        referenceId,
      referenced,
    }
  }

  if (fieldType === 'number') {
    const numericValue =
      typeof rawValue === 'number'
        ? rawValue
        : isRecord(rawValue) && typeof rawValue.value === 'number'
          ? rawValue.value
          : typeof rawValue === 'string'
            ? Number.parseFloat(rawValue)
            : isRecord(rawValue) && typeof rawValue.value === 'string'
              ? Number.parseFloat(rawValue.value)
              : Number.NaN
    if (Number.isFinite(numericValue)) {
      return {
        kind: 'number',
        value: numericValue,
        text: String(numericValue),
      }
    }
  }

  if (fieldType === 'date') {
    const text = readOptionalString(isRecord(rawValue) ? rawValue.value : undefined, rawValue) ?? ''
    return { kind: 'date', value: text, text }
  }

  const text =
    readOptionalString(
      isRecord(rawValue) ? rawValue.value : undefined,
      isRecord(rawValue) ? rawValue.text : undefined,
      rawValue
    ) ?? ''
  return { kind: 'text', value: text, text }
}

const resolveLabelLine = (
  line: unknown,
  entity: RawRecord,
  entityType: RawRecord,
  relatedEntitiesById: ReadonlyMap<string, RawRecord>,
  resolvedEntityTypesById: ReadonlyMap<string, ResolvedEntityType>,
  entityTypesById: ReadonlyMap<string, TrellisEntityType>
) => {
  if (!isRecord(line)) return undefined
  const argumentFields = Array.isArray(line.argumentFields)
    ? line.argumentFields.map((entry) => extractId(entry)).filter((value): value is string => Boolean(value))
    : []
  const values = argumentFields.map((fieldId) => {
    const field = getRawFields(entityType).find((entry) => extractId(entry.id) === fieldId) ?? {}
    const value = toFieldValue(
      getRawFieldValue(entity, fieldId),
      field,
      relatedEntitiesById,
      resolvedEntityTypesById,
      entityTypesById
    )
    return value.text || ''
  })
  return renderLabelTemplate(readString(line.format), values).trim() || undefined
}

const getEntityLabelFromType = (
  entity: RawRecord,
  entityType: RawRecord,
  relatedEntitiesById: ReadonlyMap<string, RawRecord>,
  resolvedEntityTypesById: ReadonlyMap<string, ResolvedEntityType>,
  entityTypesById: ReadonlyMap<string, TrellisEntityType>
) => {
  const label = isRecord(entityType.label) ? entityType.label : {}
  return resolveLabelLine(
    label.primary,
    entity,
    entityType,
    relatedEntitiesById,
    resolvedEntityTypesById,
    entityTypesById
  )
}

const getEntitySecondaryLabelFromType = (
  entity: RawRecord,
  entityType: RawRecord,
  relatedEntitiesById: ReadonlyMap<string, RawRecord>,
  resolvedEntityTypesById: ReadonlyMap<string, ResolvedEntityType>,
  entityTypesById: ReadonlyMap<string, TrellisEntityType>
) => {
  const label = isRecord(entityType.label) ? entityType.label : {}
  return resolveLabelLine(
    label.secondary,
    entity,
    entityType,
    relatedEntitiesById,
    resolvedEntityTypesById,
    entityTypesById
  )
}

const toEntityTypeSnapshot = (entityType: RawRecord): TrellisEntityType | null => {
  const id = extractId(entityType.id)
  const name = readString(entityType.name)
  if (!id || !name) return null

  return {
    id,
    name,
    kind: readOptionalString(entityType.kind),
    fields: getRawFields(entityType)
      .map((field) => {
        const fieldId = extractId(field.id)
        if (!fieldId) return null
        return {
          id: fieldId,
          name: readString(field.name),
          type: resolveFieldTypeName(field),
          required: isFieldRequired(field),
          referenceTypeIds: getReferenceTypeIds(field),
        }
      })
      .filter((field): field is NonNullable<typeof field> => Boolean(field)),
  }
}

const toEntitySnapshot = (
  entity: RawRecord,
  entityType: ResolvedEntityType,
  relatedEntitiesById: ReadonlyMap<string, RawRecord>,
  resolvedEntityTypesById: ReadonlyMap<string, ResolvedEntityType>,
  entityTypesById: ReadonlyMap<string, TrellisEntityType>
): TrellisEntity | null => {
  const resolvedEntity = unwrapEntity(entity)
  const id = getEntityId(resolvedEntity)
  if (!id) return null

  const fields: TrellisEntityField[] = []
  const valuesByFieldId: Record<string, TrellisEntityFieldValue> = {}
  const valuesByFieldName: Record<string, TrellisEntityFieldValue> = {}

  entityType.snapshot.fields.forEach((field) => {
    const rawField = getRawFields(entityType.raw).find((entry) => extractId(entry.id) === field.id)
    const value = toFieldValue(
      getRawFieldValue(resolvedEntity, field.id),
      rawField ?? {},
      relatedEntitiesById,
      resolvedEntityTypesById,
      entityTypesById
    )
    const nextField: TrellisEntityField = {
      id: field.id,
      name: field.name,
      type: field.type,
      required: field.required,
      value,
    }
    fields.push(nextField)
    valuesByFieldId[field.id] = value
    valuesByFieldName[field.name] = value
  })

  return {
    id,
    type: {
      id: entityType.snapshot.id,
      name: entityType.snapshot.name,
      kind: entityType.snapshot.kind,
    },
    primaryLabel:
      getEntityLabelFromType(
        resolvedEntity,
        entityType.raw,
        relatedEntitiesById,
        resolvedEntityTypesById,
        entityTypesById
      ) ||
      getEntityLabel(resolvedEntity) ||
      id,
    secondaryLabel:
      getEntitySecondaryLabelFromType(
        resolvedEntity,
        entityType.raw,
        relatedEntitiesById,
        resolvedEntityTypesById,
        entityTypesById
      ) || getEntitySecondaryLabel(resolvedEntity),
    fields,
    valuesByFieldId,
    valuesByFieldName,
  }
}

const chunk = <T>(values: readonly T[], size: number) => {
  if (values.length === 0) return []
  const result: T[][] = []
  for (let index = 0; index < values.length; index += size) {
    result.push(values.slice(index, index + size))
  }
  return result
}

const createAuthHeaders = (auth: TrellisEntitiesAuth) => ({
  Authorization: `Bearer ${auth.accessToken}`,
  Accept: 'application/json',
})

const normalizeTypeRefName = (value: string) => value.trim().toLowerCase()

export const createTrellisEntitiesApi = ({
  auth,
  fetchImpl = fetch,
}: CreateTrellisEntitiesApiInput): TrellisEntitiesApi | undefined => {
  if (!auth?.accessToken || !auth.organizationId || !auth.entityApiUrl) {
    return undefined
  }

  const entityTypeSnapshotCache = new Map<string, Promise<TrellisEntityType | null>>()
  const resolvedEntityTypeCache = new Map<string, Promise<ResolvedEntityType | null>>()
  const rawEntityTypeCache = new Map<string, Promise<RawRecord | null>>()
  let rawEntityTypesPromise: Promise<RawRecord[]> | null = null

  const fetchJson = async (url: string): Promise<unknown> => {
    const response = await fetchImpl(url, {
      headers: createAuthHeaders(auth),
    })

    if (response.status === 401 || response.status === 403) {
      return null
    }
    if (response.status === 404) {
      return null
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`)
    }
    return response.json()
  }

  const fetchRawEntityTypeById = async (id: string): Promise<RawRecord | null> => {
    const normalizedId = normalizeId(id)
    const cached = rawEntityTypeCache.get(normalizedId)
    if (cached) return cached

    const promise = fetchJson(
      `${auth.entityApiUrl}/organizations/${auth.organizationId}/types/${encodeURIComponent(id)}`
    )
      .then((value) => (isRecord(value) ? value : null))
      .catch((error) => {
        rawEntityTypeCache.delete(normalizedId)
        throw error
      })

    rawEntityTypeCache.set(normalizedId, promise)
    return promise
  }

  const listRawEntityTypes = async (): Promise<RawRecord[]> => {
    if (rawEntityTypesPromise) return rawEntityTypesPromise

    rawEntityTypesPromise = (async () => {
      const items: RawRecord[] = []
      let pageIndex = 0

      while (pageIndex < 100) {
        const result = await fetchJson(
          `${auth.entityApiUrl}/organizations/${auth.organizationId}/types?pageIndex=${pageIndex}&pageSize=${ENTITY_TYPES_PAGE_SIZE}`
        )
        if (!isRecord(result)) break

        const pageItems = getDocuments(result, 'documents')
        items.push(...pageItems)

        pageItems.forEach((entry) => {
          const id = extractId(entry.id)
          if (!id) return
          rawEntityTypeCache.set(normalizeId(id), Promise.resolve(entry))
        })

        const totalCount = getTotalCount(result)
        if (pageItems.length < ENTITY_TYPES_PAGE_SIZE || items.length >= totalCount) break
        pageIndex += 1
      }

      return items
    })().catch((error) => {
      rawEntityTypesPromise = null
      throw error
    })

    return rawEntityTypesPromise
  }

  const resolveRawEntityType = async (ref: TrellisEntityTypeRef): Promise<RawRecord | null> => {
    if (typeof ref === 'string') {
      const trimmed = ref.trim()
      if (!trimmed) return null
      if (isUuid(trimmed)) return fetchRawEntityTypeById(trimmed)
      const types = await listRawEntityTypes()
      const normalizedName = normalizeTypeRefName(trimmed)
      return types.find((candidate) => normalizeTypeRefName(readString(candidate.name)) === normalizedName) ?? null
    }

    if (ref.id?.trim()) {
      return fetchRawEntityTypeById(ref.id.trim())
    }
    if (ref.name?.trim()) {
      const types = await listRawEntityTypes()
      const normalizedName = normalizeTypeRefName(ref.name)
      return types.find((candidate) => normalizeTypeRefName(readString(candidate.name)) === normalizedName) ?? null
    }
    return null
  }

  const getEntityTypeSnapshot = async (ref: TrellisEntityTypeRef): Promise<TrellisEntityType | null> => {
    const cacheKey =
      typeof ref === 'string'
        ? `${isUuid(ref) ? 'id' : 'name'}:${normalizeTypeRefName(ref)}`
        : ref.id?.trim()
          ? `id:${normalizeId(ref.id)}`
          : `name:${normalizeTypeRefName(ref.name ?? '')}`

    const cached = entityTypeSnapshotCache.get(cacheKey)
    if (cached) return cached

    const promise = resolveRawEntityType(ref)
      .then((rawType) => (rawType ? toEntityTypeSnapshot(rawType) : null))
      .catch((error) => {
        entityTypeSnapshotCache.delete(cacheKey)
        throw error
      })

    entityTypeSnapshotCache.set(cacheKey, promise)
    return promise
  }

  const getResolvedEntityType = async (ref: TrellisEntityTypeRef): Promise<ResolvedEntityType | null> => {
    const cacheKey =
      typeof ref === 'string'
        ? `${isUuid(ref) ? 'id' : 'name'}:${normalizeTypeRefName(ref)}`
        : ref.id?.trim()
          ? `id:${normalizeId(ref.id)}`
          : `name:${normalizeTypeRefName(ref.name ?? '')}`

    const cached = resolvedEntityTypeCache.get(cacheKey)
    if (cached) return cached

    const promise = resolveRawEntityType(ref)
      .then((rawType) => {
        if (!rawType) return null
        const snapshot = toEntityTypeSnapshot(rawType)
        return snapshot ? { raw: rawType, snapshot } : null
      })
      .catch((error) => {
        resolvedEntityTypeCache.delete(cacheKey)
        throw error
      })

    resolvedEntityTypeCache.set(cacheKey, promise)
    return promise
  }

  const fetchRawEntitiesBatch = async (ids: string[], includeRelated: boolean) => {
    if (ids.length === 0) {
      return {
        documents: [] as RawRecord[],
        relatedDocuments: [] as RawRecord[],
      }
    }

    const encodedIds = encodeURIComponent(ids.join(','))
    const result = await fetchJson(
      `${auth.entityApiUrl}/organizations/${auth.organizationId}/entities?ids=${encodedIds}&related=${
        includeRelated ? 'true' : 'false'
      }&pageIndex=0&pageSize=${ids.length}`
    )

    if (!isRecord(result)) {
      return {
        documents: [] as RawRecord[],
        relatedDocuments: [] as RawRecord[],
      }
    }

    return {
      documents: getDocuments(result, 'documents'),
      relatedDocuments: getDocuments(result, 'relatedDocuments'),
    }
  }

  const fetchRawEntitiesOfType = async ({
    typeId,
    query,
    limit,
    cursor,
    includeRelated,
  }: {
    typeId: string
    query?: string
    limit: number
    cursor?: string
    includeRelated: boolean
  }) => {
    const params = new URLSearchParams({
      related: includeRelated ? 'true' : 'false',
      pageSize: String(limit),
    })
    if (cursor) params.append('cursor', cursor)
    if (query) params.append('query', query)

    const result = await fetchJson(
      `${auth.entityApiUrl}/organizations/${auth.organizationId}/types/${encodeURIComponent(typeId)}/entities?${params.toString()}`
    )
    if (!isRecord(result)) {
      return {
        documents: [] as RawRecord[],
        relatedDocuments: [] as RawRecord[],
        nextCursor: undefined as string | undefined,
      }
    }
    return {
      documents: getDocuments(result, 'documents'),
      relatedDocuments: getDocuments(result, 'relatedDocuments'),
      nextCursor: getNextCursor(result),
    }
  }

  const fetchEntityCount = async (typeId: string, query?: string) => {
    const params = new URLSearchParams()
    if (query) params.append('query', query)

    const response = await fetchImpl(
      `${auth.entityApiUrl}/organizations/${auth.organizationId}/types/${encodeURIComponent(typeId)}/entities/count?${params.toString()}`,
      {
        headers: createAuthHeaders(auth),
      }
    )

    if (response.status === 401 || response.status === 403 || response.status === 404) {
      return 0
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`)
    }

    const result = await response.json()
    return isRecord(result) && typeof result.count === 'number' && Number.isFinite(result.count) ? result.count : 0
  }

  const buildResolvedEntityTypesById = async (typeIds: string[]) => {
    const uniqueIds = Array.from(new Set(typeIds.map((id) => normalizeId(id)).filter(Boolean)))
    const entries = await Promise.all(
      uniqueIds.map(async (typeId) => {
        const type = await getResolvedEntityType({ id: typeId })
        return [typeId, type] as const
      })
    )
    return new Map(entries.flatMap(([typeId, type]) => (type ? [[typeId, type] as const] : [])))
  }

  const getMany = async (ids: string[], options?: { includeRelated?: boolean }) => {
    const normalizedIds = ids.map((id) => id.trim()).filter(Boolean)
    if (normalizedIds.length === 0) return []

    const batches = await Promise.all(
      chunk(normalizedIds, ENTITIES_BATCH_LIMIT).map((batch) =>
        fetchRawEntitiesBatch(batch, options?.includeRelated !== false)
      )
    )

    const documents = batches.flatMap((batch) => batch.documents)
    const relatedDocuments = batches.flatMap((batch) => batch.relatedDocuments)
    const entityTypeIds = [...documents, ...relatedDocuments]
      .map((entity) => getEntityTypeId(entity))
      .filter((value): value is string => Boolean(value))
    const resolvedEntityTypesById = await buildResolvedEntityTypesById(entityTypeIds)
    const entityTypesById = new Map(
      Array.from(resolvedEntityTypesById.entries()).map(
        ([typeId, entityType]) => [typeId, entityType.snapshot] as const
      )
    )

    const relatedEntitiesById = new Map<string, RawRecord>()
    ;[...documents, ...relatedDocuments].forEach((entity) => {
      const id = getEntityId(entity)
      if (!id) return
      relatedEntitiesById.set(normalizeId(id), unwrapEntity(entity))
    })

    const entitiesById = new Map<string, TrellisEntity>()
    documents.forEach((entity) => {
      const id = getEntityId(entity)
      const typeId = getEntityTypeId(entity)
      if (!id || !typeId) return
      const entityType = resolvedEntityTypesById.get(normalizeId(typeId))
      if (!entityType) return
      const snapshot = toEntitySnapshot(
        unwrapEntity(entity),
        entityType,
        relatedEntitiesById,
        resolvedEntityTypesById,
        entityTypesById
      )
      if (snapshot) {
        entitiesById.set(normalizeId(id), snapshot)
      }
    })

    return normalizedIds.flatMap((id) => {
      const snapshot = entitiesById.get(normalizeId(id))
      return snapshot ? [snapshot] : []
    })
  }

  return {
    get: async (id, options) => {
      const [entity] = await getMany([id], options)
      return entity ?? null
    },
    getMany,
    list: async ({
      type,
      query,
      limit = ENTITY_LIST_DEFAULT_LIMIT,
      cursor,
      includeRelated,
    }: TrellisEntityListInput) => {
      const entityType = await getResolvedEntityType(type)
      if (!entityType) {
        return { items: [] }
      }

      const page = await fetchRawEntitiesOfType({
        typeId: entityType.snapshot.id,
        query: query?.trim() || undefined,
        limit: Math.max(1, Math.floor(limit)),
        cursor: cursor?.trim() || undefined,
        includeRelated: includeRelated !== false,
      })

      const relatedEntitiesById = new Map<string, RawRecord>()
      ;[...page.documents, ...page.relatedDocuments].forEach((entity) => {
        const id = getEntityId(entity)
        if (!id) return
        relatedEntitiesById.set(normalizeId(id), unwrapEntity(entity))
      })

      const relatedTypeIds = [...page.documents, ...page.relatedDocuments]
        .map((entity) => getEntityTypeId(entity))
        .filter((value): value is string => Boolean(value))
      const resolvedEntityTypesById = await buildResolvedEntityTypesById([entityType.snapshot.id, ...relatedTypeIds])
      const entityTypesById = new Map(
        Array.from(resolvedEntityTypesById.entries()).map(
          ([typeId, resolvedType]) => [typeId, resolvedType.snapshot] as const
        )
      )

      const items = page.documents.flatMap((entity) => {
        const snapshot = toEntitySnapshot(
          unwrapEntity(entity),
          entityType,
          relatedEntitiesById,
          resolvedEntityTypesById,
          entityTypesById
        )
        return snapshot ? [snapshot] : []
      })

      return {
        items,
        nextCursor: page.nextCursor,
      }
    },
    count: async ({ type, query }) => {
      const entityType = await getEntityTypeSnapshot(type)
      if (!entityType) return 0
      return fetchEntityCount(entityType.id, query?.trim() || undefined)
    },
    types: {
      get: (ref) => getEntityTypeSnapshot(ref),
      list: async () => {
        const rawTypes = await listRawEntityTypes()
        return rawTypes.flatMap((entityType) => {
          const snapshot = toEntityTypeSnapshot(entityType)
          return snapshot ? [snapshot] : []
        })
      },
    },
    me: {
      id: auth.userEntityId || undefined,
      get: async () => {
        if (!auth.userEntityId) return null
        return getMany([auth.userEntityId], { includeRelated: true }).then((items) => items[0] ?? null)
      },
    },
  }
}
